import { useEffect, useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Box, Grid, Toolbar, IconButton, FormControl, InputLabel, Select, MenuItem, TextField, Typography, Button } from '@mui/material'
import { ArrowBackIos } from '@mui/icons-material'
import { useStylesTheme } from '../../config'
import { MachineButton } from '../../components/buttons'
import { useNavigate } from 'react-router-dom'
import { useAuth, useMachineContext, useMasterData, useSitesContext, useUser } from '../../context'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTruck } from '@fortawesome/free-solid-svg-icons'

const MachinesPage = ({ route }) => {
    const { userData } = useAuth()
    const { imagenesGenericasOfLine } = useMasterData()
    const { machines, machinesBySite, setMachinesListSearchTerm } = useMachineContext()
    const { sitesToSelection, siteSelected, setSiteSelected } = useSitesContext()
    const { admin, isSuperAdmin, isSapExecutive, isChiefMachinery, isShiftManager, isUniversalInspector, isUniversalChief } = useUser()
    const navigate = useNavigate()
    const [routeData, setRouteData] = useState()
    const [searchTerm, setSearchTerm] = useState('')

    const urlImagenes = 'https://icvmantencion.blob.core.windows.net/plataforma-mantencion/maquinas/imagenes/'

    const isExecutive = admin || isSuperAdmin || isSapExecutive || isChiefMachinery || isShiftManager || isUniversalInspector || isUniversalChief

    // Filter available sites based on role permissions
    const availableSites = useMemo(() => {
        if (isExecutive) {
            return sitesToSelection.filter(s => s._id !== 'universal')
        } else {
            return sitesToSelection.filter(s => s._id !== 'all' && s._id !== 'universal')
        }
    }, [sitesToSelection, isExecutive])

    // Auto-select first site for non-executives if siteSelected is 'all', 'universal', or null
    useEffect(() => {
        if (!isExecutive && availableSites.length > 0) {
            if (!siteSelected || siteSelected._id === 'all' || siteSelected._id === 'universal') {
                setSiteSelected(availableSites[0])
            }
        }
    }, [availableSites, siteSelected, isExecutive, setSiteSelected])

    // Filter machines in site
    const machinesInSite = useMemo(() => {
        if (!machines || !machinesBySite) return []

        const isAllSites = !siteSelected || siteSelected.idObra === 'none' || siteSelected._id === 'all' || siteSelected._id === 'universal'

        // Explicitly filter machinesBySite by selected site
        let filteredMachinesBySite = machinesBySite
        if (!isAllSites) {
            filteredMachinesBySite = machinesBySite.filter(m => m.idobra === siteSelected.idObra || m.obra === siteSelected._id)
        } else if (!admin && !isSuperAdmin && !isUniversalInspector && !isUniversalChief && userData?.obras) {
            const userObras = userData.obras.map(o => typeof o === 'object' && o !== null ? (o.idObra || o.idobra || o._id || o.id) : o).filter(Boolean).map(String)
            filteredMachinesBySite = machinesBySite.filter(m => userObras.includes(String(m.idobra)) || userObras.includes(String(m.obra)))
        }

        return machines.filter(mop =>
            filteredMachinesBySite.some(m =>
                m.type === mop.type &&
                m.brand === mop.brand &&
                (m.model === mop.modelo || m.modelo === mop.modelo)
            )
        )
    }, [machines, machinesBySite, siteSelected, admin, isSuperAdmin, isUniversalInspector, isUniversalChief, userData])

    // Filter and group machines to display
    const displayMachines = useMemo(() => {
        if (siteSelected?._id === 'universal') return []

        // Agrupar máquinas por Tipo, Marca y Modelo
        const groups = machinesInSite.reduce((acc, machine) => {
            if (machine.type === null || machine.type === 'Sin Asignar') return acc

            const key = `${machine.type}-${machine.brand}-${machine.modelo}`
            if (!acc[key]) {
                acc[key] = machine
            }
            return acc
        }, {})

        const groupedList = Object.values(groups)

        // Filter by searchTerm (brand or model)
        if (!searchTerm) return groupedList

        const term = searchTerm.toLowerCase()
        return groupedList.filter(m =>
            (m.brand && m.brand.toLowerCase().includes(term)) ||
            (m.modelo && m.modelo.toLowerCase().includes(term)) ||
            (m.model && m.model.toLowerCase().includes(term))
        )
    }, [machinesInSite, searchTerm, siteSelected])

    useEffect(() => {
        if (route === 'inspection') {
            setRouteData('Inspección')
        } else if (route === 'maintenance') {
            setRouteData('Mantención')
        } else if (route === 'machines') {
            setRouteData('Máquinas')
        }
    }, [route])

    return (
        <Box height='90%'>
            <Grid className={'root'} container spacing={0}>
                <Grid className={'pageContainer'} item xs={12}>
                    <div className={'pageCard'}>
                        <Grid container alignItems='flex-start' justifyContent='flex-start'>
                            <div style={{ width: '100%', textAlign: 'left', padding: 10 }}>
                                <div style={{ width: '100%', textAlign: 'left', color: '#333', backgroundColor: '#fff', borderRadius: 20 }}>
                                    <Toolbar style={{ paddingLeft: 0, paddingRight: 10, backgroundColor: '#F9F9F9', borderRadius: 10, display: 'flex', flexWrap: 'wrap', gap: 10, height: 'auto', minHeight: 56, padding: '10px' }}>
                                        <Box display="flex" alignItems="center">
                                            <IconButton onClick={() => setTimeout(() => {
                                                navigate(-1)
                                            }, 500)}>
                                                <ArrowBackIos style={{ color: '#333', fontSize: 16 }}/>
                                            </IconButton>
                                            <p style={{ marginTop: 0, marginBottom: 0, fontSize: 16, fontWeight: 'bold', marginRight: 15 }}>
                                                {`${routeData}`}
                                            </p>
                                        </Box>

                                        {/* Selector de Sitio / Obra */}
                                        <FormControl size="small" style={{ minWidth: 200, marginLeft: 'auto' }}>
                                            <InputLabel id="site-select-label">Sitio / Obra</InputLabel>
                                            <Select
                                                labelId="site-select-label"
                                                value={siteSelected && siteSelected._id !== 'universal' ? siteSelected._id : ''}
                                                onChange={(e) => {
                                                    const siteObj = availableSites.find(s => s._id === e.target.value)
                                                    if (siteObj) setSiteSelected(siteObj)
                                                }}
                                                label="Sitio / Obra"
                                            >
                                                <MenuItem value="">
                                                    <em>Seleccione Obra</em>
                                                </MenuItem>
                                                {availableSites.map((s, i) => (
                                                    <MenuItem key={i} value={s._id}>
                                                        {s.idObra && s.idObra !== 'none' && s.idObra !== 'universal' ? `${s.idObra} - ${s.descripcion}` : s.descripcion}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>

                                        {/* Input de Búsqueda de Marca o Modelo */}
                                        <TextField
                                            size="small"
                                            placeholder="Buscar marca o modelo..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            style={{ minWidth: 200 }}
                                        />
                                        <Button onClick={() => setSearchTerm('')} style={{ minWidth: 100, backgroundColor: '#be2e26', color: '#fff', fontWeight: 'bold' }} variant="contained">
                                            Limpiar
                                        </Button>
                                        
                                        {/* Indicador de Modelos en Sitio */}
                                        <Box display="flex" alignItems="center" style={{ backgroundColor: '#be2e26', color: '#fff', padding: '6px 12px', borderRadius: 20, fontSize: 13, fontWeight: 'bold' }}>
                                            Modelos en Sitio: {displayMachines.length}
                                        </Box>
                                    </Toolbar>
                                </div>
                            </div>
                            <div style={{ height: 'calc(100vh - 220px)', overflowY: 'auto', width: '100%', padding: '10px 20px' }}>
                                {!siteSelected || siteSelected._id === 'universal' ? (
                                    <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                                        <Typography variant="h6" color="textSecondary" style={{ fontWeight: 'bold' }}>
                                            Debe seleccionar una obra para ver las máquinas.
                                        </Typography>
                                    </Box>
                                ) : displayMachines.length === 0 ? (
                                    <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                                        <Typography variant="h6" color="textSecondary" style={{ fontWeight: 'bold' }}>
                                            No hay máquinas asignadas a esta obra.
                                        </Typography>
                                    </Box>
                                ) : (
                                    <Grid container>
                                        {
                                            displayMachines.map((machine, index) => {
                                                return (
                                                    <Grid item xs={12} sm={12} md={6} lg={4} xl={4} key={machine.id}>
                                                        <BotonMaquina
                                                            machine={machine}
                                                            route={route}
                                                            key={index}
                                                            navigate={navigate}
                                                            imagenesGenericasOfLine={imagenesGenericasOfLine}
                                                            setMachinesListSearchTerm={setMachinesListSearchTerm}
                                                        />
                                                    </Grid>
                                                )
                                            })
                                        }
                                    </Grid>
                                )}
                            </div>
                        </Grid>
                    </div>
                </Grid>
            </Grid>
        </Box>
    )
}

const BotonMaquina = ({ machine, route, navigate, setMachinesListSearchTerm }) => {
    const newMachine = {
        brand: machine.brand,
        id: machine.id,
        model: machine.modelo,
        pIDPM: machine.pIDPM,
        type: machine.type
    }
    const hasImage = machine.urlImagen || (machine.image && machine.image.data && machine.image.data.includes('image'))
    return (
        <button className='botonMaquinaContainer' onClick={() => {
            if (setMachinesListSearchTerm) {
                setMachinesListSearchTerm(machine.modelo || machine.model || '')
            }
            navigate(`/${route}/${JSON.stringify(newMachine)}`)
        }}>
            <div 
                style={{ 
                    backgroundColor: '#fff',
                    aspectRatio: '1/1',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden'
                }} 
                className='imagenBotonMaquina'
            >
                {machine.urlImagen ? (
                    <img style={{ borderRadius: '50%', objectFit: 'cover', height: '100%', width: '100%' }} src={machine.urlImagen} />
                ) : machine.image && machine.image.data && machine.image.data.includes('image') ? (
                    <img style={{ borderRadius: '50%', objectFit: 'cover', height: '100%', width: '100%' }} src={`data:${machine.image.data}`} />
                ) : (
                    <FontAwesomeIcon icon={faTruck} style={{ fontSize: '2.5vw', minFontSize: '24px', color: '#be2e26' }} />
                )}
            </div>
            <div className='textoBotonMaquinaContainer'>
                <p><strong>{`${machine.type}`}</strong></p>
                <p>{`${machine.brand} ${machine.modelo}`}</p>
            </div>
        </button>
    )
}

MachinesPage.propTypes = {
    route: PropTypes.oneOf(['machines'])
}

export default MachinesPage
